import React from 'react';
import type { EmailAlertsSettings, InspectionReminderSettings, NotificationSettingsState } from './settingsTypes';

type NotificationsSettingsTabProps = {
  settings: NotificationSettingsState;
  loading: boolean;
  onEmailAlertChange: (key: keyof EmailAlertsSettings, value: boolean) => void;
  onReminderChange: (patch: Partial<InspectionReminderSettings>) => void;
};

const EMAIL_ALERT_ROWS: Array<{ key: keyof EmailAlertsSettings; label: string }> = [
  { key: 'inspectionAssigned', label: 'New inspection assigned to technician' },
  { key: 'inspectionCompleted', label: 'Inspection completed' },
  { key: 'deficiencyFlagged', label: 'Deficiency flagged on inspection' },
  { key: 'workOrderCreated', label: 'Work order created' },
  { key: 'workOrderStatusChanged', label: 'Work order status changed' },
  { key: 'quoteSent', label: 'Quote sent' },
  { key: 'invoiceSent', label: 'Invoice sent' },
  { key: 'invoiceOverdue', label: 'Invoice overdue' }
];

export default function NotificationsSettingsTab({
  settings,
  loading,
  onEmailAlertChange,
  onReminderChange
}: NotificationsSettingsTabProps): React.JSX.Element {
  return (
    <div className="settings-tab-stack">
      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Email alerts</div>
        <p className="settings-hint">Choose which events send email and mobile push notifications to you.</p>

        <ul className="settings-toggle-list">
          {EMAIL_ALERT_ROWS.map((row) => (
            <li key={row.key} className="settings-toggle-row">
              <span className="settings-toggle-label">{row.label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={settings.emailAlerts[row.key]}
                className={`settings-switch ${settings.emailAlerts[row.key] ? 'is-on' : ''}`}
                disabled={loading}
                onClick={() => onEmailAlertChange(row.key, !settings.emailAlerts[row.key])}
              >
                <span className="settings-switch-thumb" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Inspection reminders</div>
        <p className="settings-hint">Automatic reminders before inspections are due.</p>

        <div className="form-group">
          <label className="form-label" htmlFor="settings-reminder-days">
            Send reminder email days before inspection due date
          </label>
          <input
            id="settings-reminder-days"
            className="form-input settings-number-input"
            type="number"
            min={1}
            max={90}
            disabled={loading}
            value={settings.inspectionReminder.daysBefore}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              if (!Number.isFinite(parsed)) return;
              onReminderChange({ daysBefore: parsed });
            }}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="settings-reminder-recipient">
            Reminder recipient
          </label>
          <select
            id="settings-reminder-recipient"
            className="form-input"
            disabled={loading}
            value={settings.inspectionReminder.recipient}
            onChange={(e) =>
              onReminderChange({
                recipient: e.target.value as InspectionReminderSettings['recipient']
              })
            }
          >
            <option value="admin">Admin only</option>
            <option value="technician">Assigned technician</option>
            <option value="both">Both</option>
          </select>
        </div>

        <div className="settings-toggle-row settings-toggle-row--inline">
          <span className="settings-toggle-label">Include inspection summary in reminder email</span>
          <button
            type="button"
            role="switch"
            aria-checked={settings.inspectionReminder.includeSummary}
            className={`settings-switch ${settings.inspectionReminder.includeSummary ? 'is-on' : ''}`}
            disabled={loading}
            onClick={() =>
              onReminderChange({ includeSummary: !settings.inspectionReminder.includeSummary })
            }
          >
            <span className="settings-switch-thumb" />
          </button>
        </div>
      </section>
    </div>
  );
}
